import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { Box, Button, Icons, TextField } from '@curves/ui-base';
import { ProductInfo } from '@curves/ui-shared';
import { GradientColorHeader } from '@Local/Components/Dialog/';
import useProductApi from '@Apis/useProductApi';
import { PRODUCT_SEARCH_MIN_LENGTH, REFERENCE_KIND_PRODUCT } from '../CampaignEdit.config';

// 輸入停頓多久後才發出搜尋（毫秒）
const SEARCH_DELAY = 500;

// 搜尋結果轉成 ProductInfo 格式，搜尋 API 不回傳圖片，只顯示 SKU、名稱與規格
const toProductInfo = ({ sku, productName, variantName }) => ({
    sku,
    name: productName,
    spec: variantName || undefined,
});

/**
 * 單品搜尋視窗
 * - 輸入至少 3 字才發出搜尋，以商品代碼加入或移除名單，套用到該商品的所有規格
 */
const CampaignProductSearchDialog = ({ open, title, selectedKeys, onAdd, onRemove, onClose }) => {
    const { getSearchSaleOptionsApi } = useProductApi();
    // 搜尋輸入文字
    const [keyword, setKeyword] = useState('');
    // 搜尋結果
    const [results, setResults] = useState([]);
    // 輸入停頓計時器
    const timerRef = useRef(null);
    // 最新一次要搜尋的文字，用來丟棄過期回應
    const latestTextRef = useRef('');

    // 呼叫搜尋 API，回應時文字已不是最新則丟棄
    const fetchResults = useCallback(
        async text => {
            latestTextRef.current = text;
            const resp = await getSearchSaleOptionsApi(text);
            if (latestTextRef.current !== text) return;
            setResults(Array.isArray(resp) ? resp : []);
        },
        [getSearchSaleOptionsApi],
    );

    // 元件卸載時清除計時器
    useEffect(() => () => clearTimeout(timerRef.current), []);

    // 輸入改變：不足 3 字清空結果不發請求，達 3 字停頓後才搜尋
    const handleChange = event => {
        const text = event.target.value;
        const trimmed = text.trim();
        setKeyword(text);
        clearTimeout(timerRef.current);
        if (trimmed.length < PRODUCT_SEARCH_MIN_LENGTH) {
            latestTextRef.current = '';
            setResults([]);
            return;
        }
        timerRef.current = setTimeout(() => fetchResults(trimmed), SEARCH_DELAY);
    };

    // 輸入字數是否不足，不足時顯示提示
    const isTooShort = keyword.trim().length < PRODUCT_SEARCH_MIN_LENGTH;

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ className: 'wx-65rem' }} maxWidth={false} fullWidth>
            <GradientColorHeader onClose={onClose}>{title}</GradientColorHeader>
            <DialogContent>
                <TextField label="商品名稱或 SKU" value={keyword} onChange={handleChange} InputProps={{ placeholder: '請輸入商品名稱或 SKU' }} fullWidth />
                {isTooShort && <Box className="font-color-3 mt-1">請輸入至少 3 個字搜尋商品名稱或 SKU</Box>}
                <Box className="font-color-3 mt-1 mb-2">會套用到這個商品的所有規格</Box>
                {!isTooShort && results.length === 0 && <Box className="font-color-3">查無符合的商品</Box>}
                {results.map(result => {
                    const { productID, variantID, sku, productName } = result;
                    const isSelected = selectedKeys.includes(`${REFERENCE_KIND_PRODUCT}_${productID}`);
                    // 此列對應的名單項目，以商品代碼套用所有規格
                    const item = { referenceKind: REFERENCE_KIND_PRODUCT, referenceID: productID, displayName: `${productName}（${sku}）`, isDeleted: false };
                    return (
                        <Box key={`${productID}_${variantID}`} className={clsx('campaign-product-result', isSelected && 'campaign-product-result--selected')}>
                            <Box className="campaign-product-result__info">
                                <ProductInfo product={toProductInfo(result)} showImage={false} />
                            </Box>
                            <Box className="campaign-pick-action">
                                {isSelected ? (
                                    <>
                                        <span className="campaign-pick-action__status">
                                            <Icons.Check fontSize="small" />
                                            已加入
                                        </span>
                                        <Button size="small" variant="text" color="error" onClick={() => onRemove(item)}>
                                            移除
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="small" variant="text" color="primary" onClick={() => onAdd(item)}>
                                        加入
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={onClose}>
                    完成
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CampaignProductSearchDialog;
